import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Barcode, QrCode, RefreshCw, ScanLine } from 'lucide-react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';

// Barcode/QR Code Section with LocationCode-NumericAssetId format support
interface BarcodeQRCodeSectionProps {
  readonly barcodeType?: 'barcode' | 'qrcode';
  readonly barcodeValue?: string;
  readonly onCodeChange: (barcodeType: 'barcode' | 'qrcode' | undefined, barcodeValue: string | undefined) => void;
  readonly disabled?: boolean;
  readonly locationCode?: string; // 3-4 character location code (e.g., "LHQ", "LDC")
  readonly assetNumericId?: string; // The numeric ID from the database (e.g., "75", "16")
}

export function BarcodeQRCodeSection({
  barcodeType,
  barcodeValue,
  onCodeChange,
  disabled = false,
  locationCode,
  assetNumericId
}: Readonly<BarcodeQRCodeSectionProps>): React.ReactElement {
  const [selectedType, setSelectedType] = useState<'barcode' | 'qrcode' | undefined>(barcodeType);
  const [inputValue, setInputValue] = useState(barcodeValue ?? '');
  const [showNoChangeDialog, setShowNoChangeDialog] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const barcodeCanvasRef = useRef<HTMLCanvasElement>(null);
  const qrcodeCanvasRef = useRef<HTMLCanvasElement>(null);

  // Generate code based on LocationCode-NumericAssetId format
  // Uses the actual database ID (e.g., "75") not extracted from assetId string
  const generateLocationBasedCode = () => {
    // Check if we have both required values
    if (locationCode && assetNumericId) {
      return `${locationCode}-${assetNumericId}`;
    }
    // Return null if we can't generate a location-based code
    return null;
  };

  // Generate random code (fallback)
  const generateRandomCode = (type?: 'barcode' | 'qrcode') => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    const code = `${timestamp}${random}`;
    setInputValue(code);
    onCodeChange(type || selectedType, code);
  };

  // Initialize with LocationCode-NumericAssetId format if no code exists
  useEffect(() => {
    if (!isInitialized && !barcodeValue && locationCode && assetNumericId) {
      const generatedCode = generateLocationBasedCode();
      if (generatedCode !== null) {
        setInputValue(generatedCode);
        // Don't call onCodeChange here to avoid auto-saving
        setIsInitialized(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationCode, assetNumericId, barcodeValue, isInitialized]);

  // Handle refresh button click
  const handleRefresh = () => {
    const newCode = generateLocationBasedCode();
    
    if (newCode === null) {
      // If no location or assetId, generate random
      generateRandomCode();
      return;
    }

    // Check if the new code is different from current
    if (newCode === inputValue) {
      setShowNoChangeDialog(true);
      return;
    }

    // Update with new location-based code
    setInputValue(newCode);
    onCodeChange(selectedType, newCode);
  };

  // Render barcode
  useEffect(() => {
    if (selectedType === 'barcode' && inputValue && barcodeCanvasRef.current) {
      try {
        JsBarcode(barcodeCanvasRef.current, inputValue, {
          format: 'CODE128',
          width: 2,
          height: 80,
          displayValue: true,
          fontSize: 14,
          margin: 10,
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    }
  }, [selectedType, inputValue]);

  // Render QR code
  useEffect(() => {
    if (selectedType === 'qrcode' && inputValue && qrcodeCanvasRef.current) {
      QRCode.toCanvas(qrcodeCanvasRef.current, inputValue, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      }).catch((error) => {
        console.error('Error generating QR code:', error);
      });
    }
  }, [selectedType, inputValue]);

  const handleTypeChange = (type: 'barcode' | 'qrcode') => {
    setSelectedType(type);
    
    if (!inputValue) {
      // No existing value - generate new code
      // Always try to generate location-based code first
      const locationBasedCode = generateLocationBasedCode();
      
      if (locationBasedCode !== null) {
        // We have location and asset ID - use location-based code
        setInputValue(locationBasedCode);
        onCodeChange(type, locationBasedCode);
      } else {
        // Fallback to random code if location or asset ID is missing
        generateRandomCode(type);
      }
    } else {
      // Has existing value - keep it
      onCodeChange(type, inputValue);
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    onCodeChange(selectedType, value);
  };

  const handleClear = () => {
    setSelectedType(undefined);
    setInputValue('');
    onCodeChange(undefined, '');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ScanLine className="h-5 w-5" />
          Barcode / QR Code
        </CardTitle>
        <CardDescription className="text-xs">
          Add a barcode or QR code to this asset for tracking and label printing
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Code Type Selection */}
        <div className="space-y-2">
          <Label htmlFor="codeType">Code Type</Label>
          <Select
            value={selectedType ?? 'none'}
            onValueChange={(value: string) => {
              if (value === 'none') {
                handleClear();
              } else {
                handleTypeChange(value as 'barcode' | 'qrcode');
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger id="codeType">
              <SelectValue placeholder="Select code type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="barcode">
                <div className="flex items-center gap-2">
                  <Barcode className="h-4 w-4" />
                  Barcode
                </div>
              </SelectItem>
              <SelectItem value="qrcode">
                <div className="flex items-center gap-2">
                  <QrCode className="h-4 w-4" />
                  QR Code
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Code Value Input */}
        {selectedType && (
          <>
            <div className="space-y-2">
              <Label htmlFor="codeValue">
                {selectedType === 'barcode' ? 'Barcode' : 'QR Code'} Value
              </Label>
              <div className="flex gap-2">
                <Input
                  id="codeValue"
                  value={inputValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleInputChange(e.target.value)}
                  placeholder={`Enter ${selectedType === 'barcode' ? 'barcode' : 'QR code'} value`}
                  disabled={disabled}
                  readOnly={disabled}
                  className={disabled ? 'bg-muted cursor-not-allowed' : ''}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleRefresh}
                  disabled={disabled}
                  title={locationCode && assetNumericId ? "Refresh to latest location code" : "Generate random code"}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Preview */}
            {inputValue && (
              <div className="space-y-2">
                <Label>Preview</Label>
                <div className="border rounded-md p-4 bg-white flex items-center justify-center min-h-[120px] overflow-hidden">
                  {selectedType === 'barcode' ? (
                    <canvas ref={barcodeCanvasRef} className="max-w-full h-auto" />
                  ) : (
                    <canvas ref={qrcodeCanvasRef} />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* No Change Alert Dialog */}
      <AlertDialog open={showNoChangeDialog} onOpenChange={setShowNoChangeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>No Changes Detected</AlertDialogTitle>
            <AlertDialogDescription>
              The barcode value is already up to date with the current location and asset ID.
              {inputValue && (
                <div className="mt-2 p-2 bg-muted rounded-md font-mono text-sm">
                  {inputValue}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
